import { notifications } from "@mantine/notifications";
import axios, { AxiosRequestConfig } from "axios";
import { SaleRequestStatusProps, VehicleDocumentTypes } from "./Types";
import { Colors } from "./Data";
import { baseURL } from "./Endpoints";

export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};
export const validatePassword = (password: string) => {
  return password.length >= 8;
};

export const getFinancialValueFromNumeric = (value: number | string | undefined) => {
  if (value !== undefined) {
    return parseFloat(value.toString()).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return "...";
};

export const getFullDate = (dateString: string | undefined | any) => {
  if (dateString) {
    const d = new Date(dateString);
    const MONTHS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const year = d.getFullYear();
    const month = MONTHS[d.getMonth()];
    const day = DAYS[d.getDay()];
    return `${day} ${d.getDate()} ${month}, ${year} `;
  }
  return dateString;
};
export const getHalfDate = (dateString: string | undefined) => {
  if (dateString) {
    const d = new Date(dateString);
    const MONTHS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const year = d.getFullYear();
    const month = MONTHS[d.getMonth()];

    return `${month}-${d.getDate()}-${year} `;
  }
  return dateString;
};
export const generateRandomString = (length: number) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};
export const DefaultErrorNotification = (message?: string, title?: string) => {
  notifications.show({
    color: "red",
    message: message ?? "An error occurred",
    title: title ?? "Error",
  });
};
export const DefaultWarningNotification = (message?: string, title?: string) => {
  notifications.show({
    color: "orange",
    message: message ?? "An error occurred",
    title: title ?? "Warning",
  });
};
export const DefaultSuccessNotification = (message?: string, title?: string) => {
  notifications.show({
    color: "green",
    message: message ?? "Completed!",
    title: title ?? "Success",
  });
};

export const getDifferencesInObjects = (obj1: any, obj2: any) => {
  const differences: any[] = [];
  for (const key in obj1) {
    if (!(key in obj2) || obj1[key] !== obj2[key]) {
      if (!differences.includes(key)) {
        differences.push(key);
      }
    }
  }
  for (const key in obj2) {
    if (!(key in obj1) || obj2[key] !== obj1[key]) {
      if (!differences.includes(key)) {
        differences.push(key);
      }
    }
  }
  return differences;
};
export const getPaginationTotal = (total: number | undefined, limit: number) => {
  if (total) {
    if (total <= limit) {
      return 1;
    } else {
      return Math.ceil(total / limit);
    }
  } else {
    return 1;
  }
};
export const showValidationErrors = (r: any) => {
  if (r && r.data && r.data[0]) {
    DefaultErrorNotification(r.data[0]);
  }
};

export const validatePhone = (phone: string) => {
  return phone && phone.toString().length > 7;
};
export const validateVIN = (vin: string) => {
  return vin && vin.length > 7;
};
export const trimString = (str: string, lowercase?: boolean) => {
  return str
    ? lowercase
      ? str.toString().trim().toLowerCase()
      : str.toString().trim()
    : "";
};

export const getColorFromRequestStatus = (status: SaleRequestStatusProps) => {
  return status === "fulfilled"
    ? Colors.GreenPrimary
    : status === "pending"
    ? Colors.BluePrimary
    : Colors.RedPrimary;
};
export const getColorFromVehicleStatus = (sold: boolean) => {
  return sold ? Colors.GreenPrimary : Colors.BluePrimary;
};
export const isStringEmpty = (str: string) => {
  if (str) {
    return str.length === 0;
  } else {
    return true;
  }
};

export const removeNullUndefinedFields = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
};

interface GenerateDocumentProps {
  docType: VehicleDocumentTypes;
  data?: any;
  callback: () => any;
}
export const generateDocument = async ({
  docType,
  data,
  callback,
}: GenerateDocumentProps) => {
  const urls: { url: string; docType: VehicleDocumentTypes }[] = [
    {
      url: `${baseURL}/admin/generate/mv-form`,
      docType: "mv",
    },
    {
      url: `${baseURL}/admin/generate/buyers-guide-form`,
      docType: "buyers-guide",
    },
    {
      url: `${baseURL}/admin/generate/odometer`,
      docType: "odometer-disclosure-statement",
    },
    {
      url: `${baseURL}/admin/generate/bill-of-sale`,
      docType: "bill-of-sale",
    },
    {
      url: `${baseURL}/admin/generate/buyers-order`,
      docType: "buyers-order",
    },
  ];
  const config: AxiosRequestConfig = {
    url: urls.find((u) => u.docType === docType)?.url,
    method: "POST",
    data,
    responseType: "blob",
    headers: {
      Authorization: localStorage.getItem("t"),
    },
  };
  try {
    const r = await axios.request(config);
    return r;
  } catch {
    callback();
  }
};
export const getDocumentTitle = (doc: VehicleDocumentTypes) => {
  return doc === "mv" ? "MV-1 Form" : doc.replaceAll("-", " ");
};
